// Configuração do catálogo (lista de teste, intervalos, limites de tamanho): leitura, edição e conferência na tela. Puro, sem rede nem React.
// Quem decide de verdade é o servidor (faixas, perfil, versão); aqui se confere o que dá para dizer antes de enviar e se explica a faixa.
import { DEFAULT_LIMITS } from './contentModel';

/** Os campos numéricos por seção, na ordem em que a tela os mostra: caminho no JSON, nome e explicação curta. */
export const SETTINGS_SECTIONS = [
    {
        id: 'polling', title: 'Intervalos de consulta',
        help: 'De quanto em quanto tempo cada lado confere o catálogo. Uma publicação, desativação ou restrição de consentimento demora até o intervalo do motor para valer no envio.',
        fields: [
            { path: 'polling.publishSeconds', label: 'Publicador de versões agendadas (segundos)', help: 'O backoffice procura versões agendadas que já venceram.' },
            { path: 'polling.engineRefreshSeconds', label: 'Consulta do motor ao backoffice (segundos)', help: 'O motor pergunta se há catálogo novo. Vale na próxima consulta, sem reiniciar.' },
            { path: 'polling.engineStaleMinutes', label: 'Alerta do motor sem resposta (minutos)', help: 'Sem confirmação do backoffice por esse tempo, o motor registra erro. Ele segue enviando com o último catálogo.' }
        ]
    },
    {
        id: 'email', title: 'Tamanho do e-mail',
        help: 'Os recomendados só avisam na tela. Os tetos bloqueiam o pedido de publicação.',
        fields: [
            { path: 'email.subjectSoft', label: 'Assunto: recomendado', help: 'Acima disso o assunto pode ser cortado na caixa de entrada.' },
            { path: 'email.preheaderSoft', label: 'Pré-cabeçalho: recomendado' },
            { path: 'email.buttonSoft', label: 'Texto do botão: recomendado' },
            { path: 'email.lineMax', label: 'Assunto, pré-cabeçalho e título: teto', help: 'Não pode ficar abaixo dos recomendados de assunto e pré-cabeçalho.' },
            { path: 'email.paragraphMax', label: 'Um parágrafo: teto' },
            { path: 'email.paragraphsMax', label: 'Parágrafos por e-mail: teto' },
            { path: 'email.footerMax', label: 'Rodapé: teto' },
            { path: 'email.buttonTextMax', label: 'Texto do botão: teto', help: 'Não pode ficar abaixo do recomendado do botão.' }
        ]
    },
    {
        id: 'push', title: 'Tamanho do push',
        help: 'O push aparece na tela bloqueada. O teto máximo é técnico: o motor corta em 120 e 400 caracteres.',
        fields: [
            { path: 'push.titleMax', label: 'Título: teto' },
            { path: 'push.bodyMax', label: 'Texto: teto' }
        ]
    },
    {
        id: 'test', title: 'Envio de teste',
        help: 'Quantos testes cada pessoa pode pedir por hora. Os destinos permitidos ficam na lista no topo da página.',
        fields: [
            { path: 'test.maxPerHour', label: 'Testes por pessoa por hora' }
        ]
    }
];

export const getAt = (settings, path) => path.split('.').reduce((node, key) => (node == null ? undefined : node[key]), settings);

/** Cópia de `settings` com `path` trocado, sem mexer no original. */
export const setAt = (settings, path, value) => {
    const [section, key] = path.split('.');
    return { ...settings, [section]: { ...(settings?.[section] ?? {}), [key]: value } };
};

/** A faixa aceita de um campo, ou null. */
export const boundOf = (bounds, path) => getAt(bounds, path) ?? null;

/** Uma lista por linha na caixa de texto; vírgula e ponto e vírgula também separam (colar uma lista de e-mails funciona). */
export const listToText = (list) => (Array.isArray(list) ? list.join('\n') : '');
export const textToList = (text) => String(text ?? '').split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);

const EMAIL = /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/;
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const MAX_TEST_ENTRIES = 20;

/**
 * Confere antes de enviar: número inteiro dentro da faixa, recomendados dentro dos tetos, e-mails e perfis bem formados. Devolve as mensagens
 * (vazia = pode enviar). O servidor repete tudo e é quem decide.
 */
export const validateSettings = (settings, bounds) => {
    const errors = [];
    for (const section of SETTINGS_SECTIONS) {
        for (const field of section.fields) {
            const value = getAt(settings, field.path);
            const bound = boundOf(bounds, field.path);
            if (!Number.isInteger(value)) { errors.push(`${field.label}: informe um número inteiro.`); continue; }
            if (bound && (value < bound.min || value > bound.max)) errors.push(`${field.label}: use um valor de ${bound.min} a ${bound.max}.`);
        }
    }
    const email = settings?.email ?? {};
    if (email.subjectSoft > email.lineMax) errors.push('O assunto recomendado não pode passar do teto de assunto.');
    if (email.preheaderSoft > email.lineMax) errors.push('O pré-cabeçalho recomendado não pode passar do teto de pré-cabeçalho.');
    if (email.buttonSoft > email.buttonTextMax) errors.push('O botão recomendado não pode passar do teto do texto do botão.');

    const emails = settings?.test?.emails ?? [];
    const profiles = settings?.test?.pushProfiles ?? [];
    for (const e of emails) if (!EMAIL.test(e)) errors.push(`E-mail de teste inválido: ${e}.`);
    for (const p of profiles) if (!GUID.test(p)) errors.push(`Perfil de teste inválido (use o id do perfil): ${p}.`);
    if (emails.length > MAX_TEST_ENTRIES || profiles.length > MAX_TEST_ENTRIES) errors.push(`Cada lista de destinos de teste aceita até ${MAX_TEST_ENTRIES} entradas.`);
    return errors;
};

/** Igualdade dos valores (a ordem das chaves não conta), para o botão Salvar saber se há o que salvar. */
export const sameSettings = (a, b) => {
    const norm = (v) => (Array.isArray(v) ? v.map(norm) : v && typeof v === 'object'
        ? Object.fromEntries(Object.keys(v).sort().map((k) => [k, norm(v[k])])) : v);
    return JSON.stringify(norm(a)) === JSON.stringify(norm(b));
};

/** Os limites no formato das validações do conteúdo. Sem configuração (ainda carregando), os padrões. */
export const limitsFromSettings = (settings) => {
    if (!settings?.email || !settings?.push) return DEFAULT_LIMITS;
    const { email, push } = settings;
    return {
        subjectSoft: email.subjectSoft, preheaderSoft: email.preheaderSoft, buttonSoft: email.buttonSoft,
        lineMax: email.lineMax, paragraphMax: email.paragraphMax, paragraphsMax: email.paragraphsMax, footerMax: email.footerMax, buttonTextMax: email.buttonTextMax,
        pushTitleMax: push.titleMax, pushBodyMax: push.bodyMax
    };
};

/** Quem mexeu por último, em uma frase; vazio quando a configuração nunca foi gravada. */
export const describeLastChange = (view, formatDate) => {
    if (!view || !view.version) return 'Ainda não foi gravada: valem os padrões deste ambiente.';
    const who = view.updatedBy ? ` por ${view.updatedBy.slice(0, 8)}` : '';
    return `Versão ${view.version}, gravada em ${formatDate(view.updatedAt)}${who}.`;
};
